import { useState, useRef, useCallback, useEffect } from 'react';
import { AnalyticsEvent } from '../constants/analyticsEvents';

// Web Speech API errors that allow onEnd to fire normally.
// 'no-speech': silence detected, STT stopped - caller handles silence retries
// 'aborted': recognition.abort() called intentionally
const NON_FATAL_SPEECH_ERRORS = new Set(['no-speech', 'aborted']);

// Web Speech API types - not included in all TypeScript lib configurations
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: { readonly transcript: string };
}

interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEventMap {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventMap) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechToTextOptions {
  onEvent?: (event: string, data: Record<string, unknown>) => void;
  onEnd?: (transcript: string) => void;
  onSpeculativeSilence?: (transcript: string) => void;
  onSpeechResumed?: () => void;
}

interface UseSpeechToTextReturn {
  isSupported: boolean;
  isListening: boolean;
  transcript: string;
  permissionDenied: boolean;
  serviceBlocked: boolean;
  startListening: (opts?: StartListeningOptions) => void;
  stopListening: () => void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance;

const getSpeechRecognitionConstructor = ():
  | SpeechRecognitionConstructor
  | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  );
};

// How long to wait (ms) after the last onresult before auto-stopping
// in continuous mode. This is the gap between "user stopped speaking" and
// "we treat them as done." Tuned to allow natural mid-sentence pauses
// (~0.3-1.5s) while feeling responsive once the user is truly finished.
const SILENCE_AUTO_STOP_MS = 1500;

const SPECULATIVE_SILENCE_MS = 500;

export interface StartListeningOptions {
  /** When true, recognition persists through pauses and accumulates results.
   *  A silence timer auto-stops after SILENCE_AUTO_STOP_MS of no new results. */
  continuous?: boolean;
}

export function useSpeechToText(
  options?: SpeechToTextOptions
): UseSpeechToTextReturn {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [serviceBlocked, setServiceBlocked] = useState(false);

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const transcriptRef = useRef('');
  const startTimeRef = useRef<number>(0);
  const errorFiredRef = useRef(false);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEventRef = useRef(options?.onEvent);
  onEventRef.current = options?.onEvent;
  const onEndRef = useRef(options?.onEnd);
  onEndRef.current = options?.onEnd;
  const onSpeculativeSilenceRef = useRef(options?.onSpeculativeSilence);
  onSpeculativeSilenceRef.current = options?.onSpeculativeSilence;
  const onSpeechResumedRef = useRef(options?.onSpeechResumed);
  onSpeechResumedRef.current = options?.onSpeechResumed;

  const speculativeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const speculativeFiredRef = useRef(false);
  // Set when recognition.stop() is called by the silence timer. The Web
  // Speech API may fire a final onresult after stop() to finalize interim
  // results - we must not treat that as "speech resumed" or it would abort
  // the speculative fetch we're trying to preserve.
  const recognitionStoppingRef = useRef(false);

  const isSupported = !!getSpeechRecognitionConstructor();

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const clearSpeculativeTimer = useCallback(() => {
    if (speculativeTimerRef.current !== null) {
      clearTimeout(speculativeTimerRef.current);
      speculativeTimerRef.current = null;
    }
  }, []);

  const startListening = useCallback(
    (opts?: StartListeningOptions) => {
      if (recognitionRef.current) return; // Already listening

      const SpeechRecognitionCtor = getSpeechRecognitionConstructor();
      if (!SpeechRecognitionCtor) return;

      const continuous = opts?.continuous ?? false;
      const recognition = new SpeechRecognitionCtor();
      recognition.continuous = continuous;
      recognition.interimResults = true;
      recognition.lang = navigator.language;

      recognition.onstart = () => {
        setIsListening(true);
        startTimeRef.current = Date.now();
        onEventRef.current?.(AnalyticsEvent.VOICE_INPUT_STARTED, {});
      };

      recognition.onresult = (event: SpeechRecognitionEventMap) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }

        const combined = finalTranscript + interimTranscript;
        transcriptRef.current = combined;
        setTranscript(combined);

        // In continuous mode, reset the silence auto-stop timer on every result.
        // When no new results arrive for SILENCE_AUTO_STOP_MS, we call stop()
        // which triggers the normal onend flow.
        if (continuous) {
          clearSilenceTimer();
          recognitionStoppingRef.current = false;
          silenceTimerRef.current = setTimeout(() => {
            recognitionStoppingRef.current = true;
            recognitionRef.current?.stop();
          }, SILENCE_AUTO_STOP_MS);

          if (!recognitionStoppingRef.current) {
            clearSpeculativeTimer();
            if (speculativeFiredRef.current) {
              speculativeFiredRef.current = false;
              onSpeechResumedRef.current?.();
            }
            speculativeTimerRef.current = setTimeout(() => {
              speculativeFiredRef.current = true;
              onSpeculativeSilenceRef.current?.(transcriptRef.current);
            }, SPECULATIVE_SILENCE_MS);
          }
        }
      };

      recognition.onerror = (event: { error: string }) => {
        const isNonFatal = NON_FATAL_SPEECH_ERRORS.has(event.error);
        if (!isNonFatal) {
          errorFiredRef.current = true;
        }
        clearSilenceTimer();
        clearSpeculativeTimer();
        speculativeFiredRef.current = false;
        recognitionStoppingRef.current = false;
        setIsListening(false);
        // Only clear transcript for fatal errors. Non-fatal errors (no-speech,
        // aborted) may fire after interim results were already shown - preserve
        // whatever was accumulated so onEnd receives it.
        if (!isNonFatal) {
          transcriptRef.current = '';
          setTranscript('');
        }
        recognitionRef.current = null;
        if (event.error === 'not-allowed') {
          setPermissionDenied(true);
        }
        if (event.error === 'network') {
          setServiceBlocked(true);
        }
        onEventRef.current?.(AnalyticsEvent.VOICE_INPUT_ERROR, {
          errorType: event.error,
        });
      };

      // Note: onend always fires after onerror in the Web Speech API.
      // The errorFiredRef prevents a spurious completed event after fatal errors.
      // Non-fatal errors (no-speech, aborted) allow onEnd to fire normally.
      recognition.onend = () => {
        clearSilenceTimer();
        clearSpeculativeTimer();
        speculativeFiredRef.current = false;
        recognitionStoppingRef.current = false;
        setIsListening(false);
        recognitionRef.current = null;

        if (!errorFiredRef.current) {
          const durationMs = Date.now() - startTimeRef.current;
          onEventRef.current?.(AnalyticsEvent.VOICE_INPUT_COMPLETED, {
            transcriptLength: transcriptRef.current.length,
            durationMs,
          });
          onEndRef.current?.(transcriptRef.current);
        }

        // Clear after onEnd reads it; otherwise consumers replay stale
        // transcripts over post-dictation edits.
        transcriptRef.current = '';
        setTranscript('');
      };

      recognitionRef.current = recognition;
      errorFiredRef.current = false;
      transcriptRef.current = '';
      setTranscript('');
      recognition.start();
    },
    [clearSilenceTimer, clearSpeculativeTimer]
  );

  const stopListening = useCallback(() => {
    clearSilenceTimer();
    clearSpeculativeTimer();
    speculativeFiredRef.current = false;
    recognitionRef.current?.stop();
  }, [clearSilenceTimer, clearSpeculativeTimer]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  return {
    isSupported,
    isListening,
    transcript,
    permissionDenied,
    serviceBlocked,
    startListening,
    stopListening,
  };
}
